import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import apiClient from '../services/apiClient';
import { useAuth } from '../hook_au/useAuth';
import {
  DEFAULT_APP_SETTINGS,
  normalizeAppSettings,
  type AppSettings,
  type AppSettingsApiResponse,
} from '../types/settings';

type CurrencyFormatOptions = {
  emptyLabel?: string;
  showZero?: boolean;
  minimumFractionDigits?: number;
  maximumFractionDigits?: number;
};

type PercentFormatOptions = {
  emptyLabel?: string;
  minimumFractionDigits?: number;
  maximumFractionDigits?: number;
};

interface AppSettingsContextValue {
  settings: AppSettings;
  isLoading: boolean;
  refreshSettings: () => Promise<void>;
  formatCurrency: (value?: string | number | null, options?: CurrencyFormatOptions) => string;
  formatPercent: (value?: string | number | null, options?: PercentFormatOptions) => string;
  applyIva: (value?: string | number | null) => number;
  currencySymbol: string;
}

const EMPTY_LABEL = '—';
const AppSettingsContext = createContext<AppSettingsContextValue | null>(null);

const toNumber = (value: string | number | null | undefined) => {
  const parsed = Number(value ?? 0);
  return Number.isFinite(parsed) ? parsed : 0;
};

const resolveCurrencySymbol = (currencyCode: string) => {
  if (currencyCode === 'EUR') return '€';
  if (currencyCode === 'USD') return '$';
  return currencyCode;
};

export function AppSettingsProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated, token } = useAuth();
  const [settings, setSettings] = useState<AppSettings>(DEFAULT_APP_SETTINGS);
  const [isLoading, setIsLoading] = useState(false);

  const refreshSettings = useCallback(async () => {
    if (!token) {
      setSettings(DEFAULT_APP_SETTINGS);
      return;
    }

    setIsLoading(true);
    try {
      const response = await apiClient.get<AppSettingsApiResponse>('/settings');
      setSettings(normalizeAppSettings(response.data));
    } catch {
      setSettings(DEFAULT_APP_SETTINGS);
    } finally {
      setIsLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (!isAuthenticated || !token) {
      setSettings(DEFAULT_APP_SETTINGS);
      setIsLoading(false);
      return;
    }
    void refreshSettings();
  }, [isAuthenticated, token, refreshSettings]);

  const formatCurrency = useCallback(
    (value?: string | number | null, options: CurrencyFormatOptions = {}) => {
      const numeric = toNumber(value);
      if (!Number.isFinite(numeric)) return options.emptyLabel ?? EMPTY_LABEL;
      if (!options.showZero && numeric === 0) return options.emptyLabel ?? EMPTY_LABEL;

      return new Intl.NumberFormat(settings.currencyLocale, {
        style: 'currency',
        currency: settings.currencyCode,
        minimumFractionDigits: options.minimumFractionDigits,
        maximumFractionDigits: options.maximumFractionDigits ?? 2,
      }).format(numeric);
    },
    [settings.currencyCode, settings.currencyLocale],
  );

  const formatPercent = useCallback(
    (value?: string | number | null, options: PercentFormatOptions = {}) => {
      const numeric = toNumber(value);
      if (!Number.isFinite(numeric)) return options.emptyLabel ?? EMPTY_LABEL;
      return new Intl.NumberFormat(settings.currencyLocale, {
        style: 'percent',
        minimumFractionDigits: options.minimumFractionDigits,
        maximumFractionDigits: options.maximumFractionDigits ?? 2,
      }).format(numeric / 100);
    },
    [settings.currencyLocale],
  );

  const applyIva = useCallback(
    (value?: string | number | null) => {
      const numeric = toNumber(value);
      return numeric * (1 + settings.ivaRatePct / 100);
    },
    [settings.ivaRatePct],
  );

  const contextValue = useMemo<AppSettingsContextValue>(
    () => ({
      settings,
      isLoading,
      refreshSettings,
      formatCurrency,
      formatPercent,
      applyIva,
      currencySymbol: resolveCurrencySymbol(settings.currencyCode),
    }),
    [
      settings,
      isLoading,
      refreshSettings,
      formatCurrency,
      formatPercent,
      applyIva,
    ],
  );

  return <AppSettingsContext.Provider value={contextValue}>{children}</AppSettingsContext.Provider>;
}

export function useAppSettings() {
  const context = useContext(AppSettingsContext);
  if (!context) {
    throw new Error('useAppSettings debe usarse dentro de AppSettingsProvider');
  }
  return context;
}
